// @ts-nocheck
import React, { useState } from 'react';
import {
	BooleanInput,
	Datagrid,
	EditBase,
	FunctionField,
	NumberInput,
	ReferenceInput,
	ReferenceManyField,
	required,
	SelectInput,
	SimpleForm,
	TextField,
	TextInput,
	TopToolbar,
	useRecordContext,
	Button as RaButton,
} from 'react-admin';
import { Card, CardContent, Typography, Grid, Button } from '@mui/material';
import { Route, Routes, useNavigate } from 'react-router';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StoreIcon from '@mui/icons-material/Store';
import StorageIcon from '@mui/icons-material/Storage';
import ChatIcon from '@mui/icons-material/Chat';
import ConfirmDeleteButton from '../../Components/Button/ConfirmDeleteBtn';
import ProjectUserModalCreate from './ProjectUsers/ProjectUserModalCreate';
import { ProjectQdrant } from './ProjectQdrant';
import { ProjectChat } from './ProjectChat';
import { ProjectAgentsBudget } from './ProjectAgentsBudget';
import { ActivityCard } from '../Activities/ActivityCard';
import UserIdentityField from '../../Components/Fields/UserIdentityField';

// #region ProjectActivityCard
// ⚠️ Le flux d'activité s'indexe sur le FOLDER ID du projet, pas sur sa clé primaire :
// c'est l'identifiant que portent les events du bus. Rien à afficher tant que le record
// n'est pas chargé — une carte vide se lirait comme une absence d'activité.
const ProjectActivityCard = () => {
	const record = useRecordContext();
	if (!record?.folderId) return null;
	// Colonne projet masquée : elle répéterait la fiche courante à chaque ligne.
	return <ActivityCard filters={{ projectFolderId: record.folderId }} showProject={false} />;
};
// #endregion

// #region ProjectActions
const ProjectActions = () => {
	const record = useRecordContext();
	const navigate = useNavigate();

	if (!record) return null;

	// Lien vers le projet côté CLIENT (ui-public).
	//
	// ⚠️ La route front s'indexe sur le FOLDER ID, pas sur la clé primaire du record :
	// `HOME: '/:projectId'` dans ui-public, où `projectId` est le folderId (même
	// convention que le lien de LogView). Utiliser `record.id` mènerait à un projet
	// inexistant, ou pire, à celui de quelqu'un d'autre.
	//
	// Pas de bouton si le folderId manque : un lien vers `.../undefined` est une
	// impasse qui se lit comme une panne du front.
	const frontUrl = record.folderId ? `${(window as any).FRONT_URL}/${record.folderId}` : '';

	const handleGoHome = () => {
		navigate(`/projects/${record.id}`);
	};

	const handleGoQdrant = () => {
		navigate(`/projects/${record.id}/qdrant`);
	};

	const handleGoChat = () => {
		navigate(`/projects/${record.id}/chat`);
	};

	return (
		<TopToolbar>
			<RaButton label="Home" onClick={handleGoHome}>
				<HomeIcon />
			</RaButton>
			{/* Nouvel onglet : ui-public est une AUTRE application. Y naviguer dans le même
			    onglet ferait perdre la fiche admin en cours, et le retour arrière ramènerait
			    sur un écran rechargé. */}
			{frontUrl && (
				<RaButton
					label="Voir côté client"
					href={frontUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					<OpenInNewIcon />
				</RaButton>
			)}
			{/* Billing account : navigation INTERNE (même app), donc pas de nouvel onglet. */}
			{record.billingAccountId && (
				<RaButton
					label="Billing account"
					onClick={() => navigate(`/billing-accounts/${record.billingAccountId}`)}
				>
					<StoreIcon />
				</RaButton>
			)}
			<RaButton label="Qdrant" onClick={handleGoQdrant}>
				<StorageIcon />
			</RaButton>
			<RaButton label="Chat" onClick={handleGoChat}>
				<ChatIcon />
			</RaButton>
		</TopToolbar>
	);
};
// #endregion

// #region ProjectUsersCard
const ProjectUsersCard = () => {
	const [open, setOpen] = useState(false);

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<Card>
			<CardContent>
				<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
					<Typography variant="h6" gutterBottom>
						Associated Users
					</Typography>
					<Button variant="contained" color="primary" onClick={handleOpen}>
						+
					</Button>
				</div>
				<ReferenceManyField
					label="Projects"
					reference="project-users"
					target="projectId"
					sort={{ field: 'user.name', order: 'ASC' }}
				>
					<Datagrid>
						<UserIdentityField label="User" />
						<TextField source="role" label="Role" />
						<FunctionField
							render={(record) => (
								<ConfirmDeleteButton
									record={record}
									title="Delete user on project user"
									entity="project-users"
									description="Are you sure to delete this user ?"
									withRedirect={false}
									confirmMessage="User is deleted"
								/>
							)}
						/>
					</Datagrid>
				</ReferenceManyField>
			</CardContent>
			<ProjectUserModalCreate open={open} handleClose={handleClose} />
		</Card>
	);
};
// #endregion

// #region ProjectDetailsCard
const ProjectDetailsCard = () => {
	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Project Details
				</Typography>
				<SimpleForm>
					<TextInput source="id" disabled />
					<ReferenceInput
						label="Billing Account"
						source="billingAccountId"
						reference="billing-accounts"
					>
						<SelectInput optionText="name" validate={required()} />
					</ReferenceInput>

					<TextInput source="name" />
					<TextInput source="llmDescription" multiline rows={4} label="LLM Description" />
					<TextInput source="folderId" disabled />
					<TextInput source="projectDatawareHouseId" disabled />
					<TextInput source="accountEmail" disabled />
					<TextInput source="region" disabled />

					{/* Réservé à Quanti : un projet interne sort du périmètre de mesure et de
					    facturation du warehouse managé. Jamais exposé côté client. */}
					<BooleanInput
						source="isInternal"
						label="Projet interne (Quanti)"
						helperText="Exclut le projet de la mesure et de la facturation du warehouse managé."
					/>

					{/* Plafond de coût par process d'ingestion. Réglage interne depuis
					    QV2-4853 : retiré des réglages client, où il se confondait avec la
					    jauge de budget managé. Vaut pour tous les comptes connecteurs du
					    projet, sauf override au niveau du connecteur. */}
					<BooleanInput
						source="datawarehouseCostLimitEnabled"
						label="Process cost limit enabled"
						helperText="Stops any ingestion process that exceeds the limit below"
					/>
					<NumberInput
						source="datawarehouseCostLimit"
						label="Process cost limit"
						min={0}
						step={0.01}
						helperText="USD, or Snowflake credits on a Snowflake project"
					/>
				</SimpleForm>
			</CardContent>
		</Card>
	);
};
// #endregion

// #region ProjectHome
const ProjectHome = () => {
	return (
		<Grid container spacing={2}>
			<Grid item xs={8}>
				<ProjectUsersCard />
				<ProjectActivityCard />
			</Grid>
			<Grid item xs={4}>
				<ProjectDetailsCard />
				<ProjectAgentsBudget />
			</Grid>
		</Grid>
	);
};
// #endregion

// #region ProjectEdit
export const ProjectEdit = (props: any) => {
	return (
		<EditBase {...props} resource="projects">
			<>
				<ProjectActions />
				<Routes>
					<Route index element={<ProjectHome />} />
					<Route path="qdrant" element={<ProjectQdrant />} />
					<Route path="chat" element={<ProjectChat />} />
				</Routes>
			</>
		</EditBase>
	);
};
// #endregion
